import { useState } from 'react';
import { Navigate } from 'react-router';
import { toApiErrorMessage } from '@/lib/api-client';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAcceptTerms, useTerms } from './legal-api';

/**
 * The agreement, and the button that records having read it.
 *
 * Deliberately plain. Everything on this page is either the text itself or the
 * act of agreeing to it, because anything else here is a distraction from the
 * one thing the person is being asked to do.
 *
 * The checkbox is not decoration: a single button click is a weaker record than
 * a deliberate tick followed by a click, and this page exists to produce a
 * record.
 */
export function TermsPage() {
  const { data, isLoading } = useTerms();
  const accept = useAcceptTerms();
  const [confirmed, setConfirmed] = useState(false);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  // Nothing to agree to, or already agreed. Reaching this page directly should
  // not strand somebody on a dead end.
  if (!data || !data.requireAcceptance || !data.body.trim() || data.accepted) {
    return <Navigate to="/" replace />;
  }

  const returning = data.acceptedVersion !== null;

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold">Terms of use</h1>
        <p className="text-sm text-muted-foreground">
          {returning
            ? 'These terms have been updated. Please read them again before continuing.'
            : 'Please read these before using the system.'}
          {' '}Version {data.version}.
        </p>
      </div>

      {data.summary.trim() && (
        <Alert>
          <AlertDescription>{data.summary}</AlertDescription>
        </Alert>
      )}

      {/*
        Scrolls in its own box rather than running down the page, so the button
        stays visible and nobody has to hunt for it after a long read.
        `whitespace-pre-wrap` renders the text exactly as it was written — this
        is an agreement, and reflowing somebody's paragraphs is not this page's
        job.
      */}
      <div className="max-h-[55vh] overflow-y-auto rounded-md border bg-muted/30 p-4">
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{data.body}</p>
      </div>

      {accept.isError && (
        <Alert variant="destructive">
          <AlertDescription>{toApiErrorMessage(accept.error)}</AlertDescription>
        </Alert>
      )}

      {data.mustAcceptOnPhone ? (
        /*
          No checkbox and no button on a desktop.

          The server refuses this browser anyway, so offering the control would
          be offering something that always errors. The flag comes from the
          server reading this request's own user-agent, not from the browser
          describing itself, so what is shown and what is enforced are the same
          judgement.
        */
        <Alert>
          <AlertDescription>
            <strong>Open this on your phone to accept.</strong> These terms describe what the
            app records from your phone while you are working, so they are agreed there rather
            than at a desk. Sign in at hr.kiobytes.com on your handset — everything else in the
            system still works here.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-4 shrink-0"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <span>I have read and agree to these terms.</span>
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              disabled={!confirmed}
              loading={accept.isPending}
              onClick={() => accept.mutate(data.version)}
            >
              Agree and continue
            </Button>
            <p className="text-xs text-muted-foreground">
              Your name, the version above and the time are recorded.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
